import { filter, find, includes, isEmpty, map, some } from 'es-toolkit/compat';

import { FlowClassName, ResultType } from 'interfaces/experimentResults';
import { SlideResultInventory } from './useSlideChannelsAndResults/slideResultInventory';

export const resultReviewIntentParam = 'resultReviewIntent' as const;

export interface ResultReviewIntent {
  version: 1;
  selector: {
    resultTypes?: ResultType[];
    flowClassNames?: FlowClassName[];
  };
  selection: 'latest_per_slide';
  view: 'overlays' | 'features';
  activate: 'all';
  // Optional overlay opacity, 0-100. Absent means the existing layer opacity stays unchanged.
  opacity?: number;
}

export interface ResolvedResultReviewIntent {
  resultGroupKey: string;
  experimentResultIds: number[];
}

const matchesResultReviewSelector = (
  result: SlideResultInventory['orchestrationGroups'][number]['sourceResults'][number]['result'],
  intent: ResultReviewIntent
) =>
  includes(intent.selector.resultTypes || [], result.resultType) ||
  includes(intent.selector.flowClassNames || [], result.flowClassName);

export const resolveResultReviewIntent = ({
  inventory,
  intent,
}: {
  inventory?: SlideResultInventory;
  intent?: ResultReviewIntent | null;
}): ResolvedResultReviewIntent | undefined => {
  if (
    !inventory ||
    intent?.version !== 1 ||
    intent.selection !== 'latest_per_slide' ||
    intent.activate !== 'all' ||
    (isEmpty(intent.selector?.resultTypes) && isEmpty(intent.selector?.flowClassNames))
  ) {
    return undefined;
  }

  const resultGroup = find(inventory.orchestrationGroups, ({ sourceResults }) =>
    some(sourceResults, ({ result }) => matchesResultReviewSelector(result, intent))
  );
  if (!resultGroup) {
    return undefined;
  }

  return {
    resultGroupKey: resultGroup.key,
    experimentResultIds: map(
      filter(resultGroup.sourceResults, ({ result }) => matchesResultReviewSelector(result, intent)),
      ({ result }) => result.experimentResultId
    ),
  };
};
